/*
 * Pure helpers for the Bugsnag reporter (see services/bugsnag.js).
 *
 * These are deliberately side-effect free so they can be unit tested in
 * isolation — they decide (a) how errors are grouped and (b) which errors are
 * worth reporting at all. Keeping the policy here, out of the client-init side
 * effects in bugsnag.js, is what makes it testable.
 */

const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

export interface StackFrame {
  file?: string;
}

export interface ErrorDescriptor {
  errorClass?: string;
  errorMessage?: string;
  code?: string;
  stacktrace?: StackFrame[];
}

/**
 * Collapse a URL (or bare path) into a stable grouping key: drop the origin,
 * query string and hash, then replace dynamic path segments (UUIDs and numeric
 * ids) with ":id". This turns "one Bugsnag group per entity" into "one group
 * per route", which is the whole point — a per-invitation failure should not
 * spawn a brand-new error every time.
 *
 * The origin is intentionally dropped so the same failure on picnic/app/
 * flexplein/... collapses into a single group instead of one per brand domain.
 */
export const normalizeGroupingKey = (url = ""): string => {
  const path = url
    .replace(/^https?:\/\/[^/]+/i, "") // strip scheme + host
    .split("#")[0] // strip hash
    .split("?")[0] // strip query string
    .replace(UUID_RE, ":id") // collapse UUID segments
    .replace(/\/\d+(?=\/|$)/g, "/:id"); // collapse numeric-id segments

  return path || "/";
};

/** The grouping hash Bugsnag should use for a report from a given location. */
export const groupingHash = (errorClass = "", url = ""): string => `${errorClass}${normalizeGroupingKey(url)}`;

// Axios surfaces transient failures via a stable `code`; message text varies by
// version/locale so we lean on the code first.
const TRANSIENT_CODES = ["ERR_NETWORK", "ECONNABORTED", "ERR_CANCELED"];

/**
 * Expected/transient failures nobody can act on: dropped connections, request
 * timeouts, and cancelled requests. Note we do NOT drop by HTTP status here —
 * 4xx and 5xx keep reporting, and normalized grouping keeps their volume sane.
 */
export const isTransientError = ({ errorMessage = "", code }: ErrorDescriptor = {}): boolean => {
  if (code && TRANSIENT_CODES.includes(code)) return true;
  if (errorMessage === "Network Error") return true;
  if (errorMessage === "timeout exceeded") return true;
  if (/^timeout of \d+ms exceeded$/.test(errorMessage)) return true;
  return false;
};

/**
 * Third-party / benign browser noise that never originates in our code:
 *  - "ResizeObserver loop ..." — a benign browser layout notification.
 *  - "Invalid call to runtime.sendMessage(). Tab not found." — a browser
 *    extension talking to itself.
 *
 * Matched on specific, full phrases (never single shared words) so a real bug
 * that merely mentions "loop" or "message" is not swallowed.
 */
export const isEnvironmentalNoise = ({ errorMessage = "" }: ErrorDescriptor = {}): boolean =>
  errorMessage.includes("ResizeObserver loop") || errorMessage.includes("Invalid call to runtime.sendMessage()");

// Every script we ship is served from /static/js/ (webpack runtime, vendor and
// lazy chunks alike), in every environment including the CRA dev server.
const OWN_SCRIPT = /\/static\/js\//;

/**
 * Errors thrown by scripts the browser injects into our page, which we can
 * neither reproduce nor fix. The giveaway is that no frame points at a file of
 * ours: injected code has no source URL of its own, so its frames are
 * attributed to the document URL.
 *
 * The case this was written for: Chrome on iOS translating an invitation into
 * another language, where Google's own translation script recurses until it
 * throws "Maximum call stack size exceeded". See services/domTranslationGuard for
 * the related React-17 crash, which is ours and stays reported.
 *
 * A report with no stack at all is kept — an unparseable stack is not evidence
 * of third-party code.
 */
export const isThirdPartyStack = ({ stacktrace }: ErrorDescriptor = {}): boolean =>
  Array.isArray(stacktrace) &&
  stacktrace.length > 0 &&
  !stacktrace.some(frame => OWN_SCRIPT.test((frame && frame.file) || ""));

/** Single predicate the reporter calls: report unless transient, noise or third-party. */
export const shouldReport = (descriptor: ErrorDescriptor = {}): boolean =>
  !isTransientError(descriptor) && !isEnvironmentalNoise(descriptor) && !isThirdPartyStack(descriptor);
